import { useEffect, useState } from "react";

const STEPS = [
  "Verifying your eligibility",
  "Reserving his body wash",
  "Activating your 60-day guarantee",
  "Loading your personalized offer",
];

/**
 * Discount-applying loader (PR #48's CLAIM MY DISCOUNT funnel exit). The buyer
 * already said yes on THE FIX, so this screen is NOT another sell: it reflects
 * that commitment back as a sequence of "applying" steps and hands ownership of
 * the body wash / guarantee to them before the funnel redirects to the live
 * Manly PDP. Anti-patterns avoided: countdowns, dollar amounts, generic
 * "loading…" copy.
 *
 * Uses the same approved-prototype tick chassis as the building loader
 * (`.qk-manly-lrow` in report.tokens.css, injected quiz-wide via
 * QuizDefinition.styles) so the two loaders read as one system. Owns its timers
 * and MUST clear them on unmount.
 */
export function ApplyingPage({ onDone, durationMs = 3400 }: { onDone: () => void; durationMs?: number }) {
  // How many steps have been reached. 0 = first step active; STEPS.length = all done.
  const [active, setActive] = useState(0);

  useEffect(() => {
    const stepMs = durationMs / (STEPS.length + 1);
    const timers = STEPS.map((_, i) =>
      window.setTimeout(() => setActive(i + 1), Math.round((i + 1) * stepMs)),
    );
    timers.push(window.setTimeout(onDone, durationMs));
    return () => timers.forEach((t) => clearTimeout(t));
  }, [onDone, durationMs]);

  return (
    <div className="qk-manly-loader" role="status" aria-live="polite">
      <h2>Applying your discount…</h2>
      <p className="qk-manly-loader-lead">Securing his body wash and loading your personalized offer.</p>

      <ol className="qk-manly-loader-rows">
        {STEPS.map((label, i) => {
          const state = active > i ? "done" : active === i ? "active" : "pending";
          return (
            <li key={label} className={`qk-manly-lrow is-${state}`}>
              <span className="qk-manly-tick" aria-hidden="true">{state === "done" ? "✓" : ""}</span>
              <span>{label}</span>
            </li>
          );
        })}
      </ol>

      <p className="qk-manly-loader-foot">Please don't refresh. Your discount is being applied.</p>
    </div>
  );
}
